import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { useNavigate } from "react-router-dom";
import moment from "moment";

// material
import {
  Box,
  Container,
  FormControl,
  MenuItem,
  Typography,
  TextField,
  Stack
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

// components
import Page from "../../../components/Page";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DatePicker from "@mui/lab/DatePicker";
import { useSelector } from "react-redux";
import { visaExtensionPostdocUpload } from "../../../core/service/visaExtensionPostdoc.service";
import { getNationality } from "../../../core/service/users.service";
import Swal from "sweetalert2";

// ----------------------------------------------------------------------
// const ROLE_ADMIN = "admin";
// const ROLE_MENTOR = "mentor";
// const ROLE_STD = "std";

export default function FormVisaExtension() {
  const { user: currentUser } = useSelector((state) => state.authReducer);
  const navigate = useNavigate();
  // const { user: currentUser } = useSelector((state) => state.authReducer);

  const isValidDate = (d) => {
    return d instanceof Date && !isNaN(d);
  };

  const FormVisaSchema = Yup.object().shape({
    passport_no: Yup.string().required("Is required"),
    expired_date_visa: Yup.string()
      .nullable()
      .required(`Expired Date Is required`)
      .test("expired_date_visa", "Expired date is not valid", (val) => {
        if (val) {
          const startDate = new Date();
          const endDate = new Date(2050, 12, 31);
          if (moment(val, moment.ISO_8601).isValid()) {
            return moment(val).isBetween(startDate, endDate);
          }
          return false;
        }
        return false;
      }),
    passport_data_page: Yup.object()
      .shape({
        file: Yup.mixed()
      })
      .nullable()
      .required(`File is required`),
    expired_date_visa_file: Yup.object()
      .shape({
        file: Yup.mixed()
      })
      .nullable()
      .required(`File is required`),
    type_visa_previously: Yup.string().required("Is required"),
    reasons_application: Yup.string().required("Is required"),
    additional_note_case: Yup.string(),
    additional_required_document: Yup.object()
      .shape({
        file: Yup.mixed()
      })
      .nullable(),
    extend_visa_family: Yup.string().required("Is required")
  });

  const formik = useFormik({
    initialValues: {
      passport_no: "",
      expired_date_visa: "",
      passport_data_page: {
        file: {}
      },
      expired_date_visa_file: {
        file: {}
      },
      type_visa_previously: "",
      reasons_application: "",
      additional_note_case: "",
      additional_required_document: {
        file: {}
      },
      extend_visa_family: ""
    },
    validationSchema: FormVisaSchema,
    onSubmit: async (values) => {
      var formData = new FormData();
      for (const [key, value] of Object.entries(values)) {
        if (value?.file) {
          formData.append(key, value.file);
        } else {
          formData.append(key, value);
        }
      }
      formData.append("user_id", currentUser.id);

      await visaExtensionPostdocUpload(formData).then((e) => {
        return Swal.fire({
          icon: "success",
          title: "Successfully",
          showConfirmButton: false,
          timer: 1500
        }).then(async (result) => {
          const profile = await getNationality(currentUser?.id);
          if (profile?.nationality !== "Thai")
            return navigate("/medical-insurance/post_doc", {
              replace: true
            });
          else
            return navigate("/renewal-std/post_doc", {
              replace: true
            });
        });
      });

      await new Promise((r) => setTimeout(r, 500));
    }
  });

  const {
    errors,
    getFieldProps,
    handleSubmit,
    handleChange,
    isSubmitting,
    touched,
    values,
    setFieldValue
  } = formik;

  return (
    <Page title="Visa Extension Letter">
      <Container maxWidth="xl">
        <Box sx={{ pb: 5 }}>
          <Typography variant="h4">Visa Extension Letter</Typography>
        </Box>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack
              direction="column"
              justifyContent="space-evenly"
              alignItems="stretch"
              spacing={4}
            >
              <Typography variant="body1">
                Please be informed, the requested letter will be issued within 3
                weeks.
              </Typography>

              <Stack direction="row" spacing={6}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2">
                    Your Passport No. *
                  </Typography>
                  <TextField
                    {...getFieldProps("passport_no")}
                    error={Boolean(touched.passport_no && errors.passport_no)}
                    helperText={touched.passport_no && errors.passport_no}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="subtitle2">
                    Expired Date of Your Visa*
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      views={["year", "month"]}
                      minDate={new Date()}
                      maxDate={new Date("2050/12/31")}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={Boolean(
                            touched.expired_date_visa &&
                              errors.expired_date_visa
                          )}
                          helperText={
                            touched.expired_date_visa &&
                            errors.expired_date_visa
                          }
                        />
                      )}
                      value={values.expired_date_visa}
                      onChange={(date) => {
                        if (isValidDate(date)) {
                          setFieldValue(
                            "expired_date_visa",
                            date.toISOString()
                          );
                        } else {
                          setFieldValue("expired_date_visa", date);
                        }
                      }}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Stack>

              <Stack direction="row" spacing={6}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2">
                    Passport Data Page *
                  </Typography>
                  <TextField
                    inputProps={{
                      accept:
                        "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
                    }}
                    type="file"
                    onChange={(event) => {
                      setFieldValue(
                        `passport_data_page.file`,
                        event.currentTarget.files[0]
                      );
                    }}
                    error={Boolean(
                      touched.passport_data_page && errors.passport_data_page
                    )}
                    helperText={
                      touched.passport_data_page && errors.passport_data_page
                    }
                  />
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="subtitle2">
                    Latest Entry Visa Page *
                  </Typography>
                  <TextField
                    inputProps={{
                      accept:
                        "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
                    }}
                    type="file"
                    onChange={(event) => {
                      setFieldValue(
                        `expired_date_visa_file.file`,
                        event.currentTarget.files[0]
                      );
                    }}
                    error={Boolean(
                      touched.expired_date_visa_file &&
                        errors.expired_date_visa_file
                    )}
                    helperText={
                      touched.expired_date_visa_file &&
                      errors.expired_date_visa_file
                    }
                  />
                </FormControl>
              </Stack>

              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  What type of visa did you previously hold?*
                </Typography>
                <TextField
                  {...getFieldProps("type_visa_previously")}
                  error={Boolean(
                    touched.type_visa_previously && errors.type_visa_previously
                  )}
                  helperText={
                    touched.type_visa_previously && errors.type_visa_previously
                  }
                />
              </FormControl>

              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  Reasons for Application*
                </Typography>
                <TextField
                  {...getFieldProps("reasons_application")}
                  error={Boolean(
                    touched.reasons_application && errors.reasons_application
                  )}
                  helperText={
                    touched.reasons_application && errors.reasons_application
                  }
                />
              </FormControl>

              <Stack direction="row" spacing={6}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2">
                    Additional Note for your case (if you have)
                  </Typography>
                  <TextField
                    multiline
                    rows={5}
                    {...getFieldProps("additional_note_case")}
                    error={Boolean(
                      touched.additional_note_case &&
                        errors.additional_note_case
                    )}
                    helperText={
                      touched.additional_note_case &&
                      errors.additional_note_case
                    }
                  />
                  <Typography variant="body1">
                    Please explain us about your case more, if you have to
                    change type of visa, cancelling the previous visa that you
                    have, before starting the new visa under C2F.
                  </Typography>
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="subtitle2">
                    Additional required document
                  </Typography>
                  <TextField
                    inputProps={{
                      accept:
                        "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
                    }}
                    type="file"
                    onChange={(event) => {
                      setFieldValue(
                        `additional_required_document.file`,
                        event.currentTarget.files[0]
                      );
                    }}
                    error={Boolean(
                      touched.additional_required_document &&
                        errors.additional_required_document
                    )}
                    helperText={
                      touched.additional_required_document &&
                      errors.additional_required_document
                    }
                  />
                </FormControl>
              </Stack>

              <Stack spacing={1}>
                <Typography variant="subtitle2">
                  Do you need to extend visa for your family?
                </Typography>
                <FormControl fullWidth>
                  <TextField
                    select
                    value={values.extend_visa_family}
                    onChange={(e) => handleChange(e)}
                    {...getFieldProps("extend_visa_family")}
                    error={Boolean(
                      touched.extend_visa_family && errors.extend_visa_family
                    )}
                    helperText={
                      touched.extend_visa_family && errors.extend_visa_family
                    }
                  >
                    <MenuItem value={"yes_need"}>Yes, I need.</MenuItem>
                    <MenuItem value={"no_need"}>No, I don't need.</MenuItem>
                  </TextField>
                </FormControl>
                <Typography variant="body1">
                  Please recheck your information before you submit the form.
                </Typography>
              </Stack>

              <Stack
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
                spacing={12}
                sx={{ paddingTop: 3 }}
              >
                <LoadingButton
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                >
                  Submit
                </LoadingButton>
              </Stack>
            </Stack>
          </Form>
        </FormikProvider>
      </Container>
    </Page>
  );
}
